import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  User, Users, Shield, AlertTriangle, FileSearch, Server, UserCheck,
  Volume2, VolumeX, MoreVertical, Play, Loader2, Settings2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import API from "../lib/api";
import { withTimeout, isTimeoutError } from "../lib/api";
import { toast } from "sonner";
import TeamConfigEditor from "./TeamConfigEditor";

const AGENT_ICONS = {
  UserCheck: UserCheck,
  AlertTriangle: AlertTriangle,
  FileSearch: FileSearch,
  Shield: Shield,
  Server: Server,
  User: User
};

const STATUS_COLORS = {
  idle: "bg-slate-500",
  analyzing: "bg-amber-500 animate-pulse",
  has_findings: "bg-emerald-500",
  clear: "bg-cyan-500",
  no_test: "bg-violet-500",
  needs_evidence: "bg-orange-500",
  muted: "bg-slate-400"
};

const STATUS_LABELS = {
  idle: "Idle",
  analyzing: "Analyzing...",
  has_findings: "Has Findings",
  clear: "No Issues Found",
  no_test: "No Executable Test",
  needs_evidence: "Needs More Evidence",
  muted: "Muted"
};

export function TeamRosterPanel({
  teamConfig,
  auditRunId,
  onUpdate,
  onRunAnalysis,
  analysisEndpoint = null,
  runButtonLabel = "Run Full Analysis",
}) {
  const [runningAnalysis, setRunningAnalysis] = useState(false);
  const [analysisPhase, setAnalysisPhase] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const { template_name, risk_appetite, agents = [] } = teamConfig || {};

  const activeAgentName = useMemo(() => {
    const active = agents.find((agent) => agent.status === "analyzing");
    return active ? active.name : "";
  }, [agents]);

  useEffect(() => {
    if (!runningAnalysis) return;
    const pollId = setInterval(() => {
      onUpdate?.();
    }, 2000);
    return () => clearInterval(pollId);
  }, [runningAnalysis, onUpdate]);

  if (!teamConfig) return null;

  const handleMuteAgent = async (agentId, currentStatus) => {
    const newStatus = currentStatus === "muted" ? "idle" : "muted";
    try {
      await API.put(`/audit-runs/${auditRunId}/team/agent-status?agent_id=${agentId}&status=${newStatus}`);
      toast.success(`Agent ${newStatus === "muted" ? "muted" : "unmuted"}`);
      onUpdate?.();
    } catch (err) {
      toast.error("Failed to update agent status");
    }
  };

  const handleRunAnalysis = async () => {
    setRunningAnalysis(true);
    try {
      // Run full analysis (all 3 phases)
      setAnalysisPhase("Independent Review...");
      const endpoint = analysisEndpoint || `/audit-runs/${auditRunId}/team/run-analysis?phase=full`;
      const { data } = await API.post(
        endpoint,
        {},
        withTimeout()
      );

      toast.success(
        `Team analysis complete! ${data.observations_generated} observations, ${data.debates_created} debates, ${data.findings_consolidated} consolidated findings.`
      );
      onRunAnalysis?.(data);
      onUpdate?.();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(isTimeoutError(err) ? "Analysis timed out. Please try again." : "Analysis failed: " + detail);
    } finally {
      setRunningAnalysis(false);
      setAnalysisPhase("");
    }
  };

  const handleSaveConfig = async (nextConfig) => {
    setSavingConfig(true);
    try {
      await API.put(`/audit-runs/${auditRunId}/team/config`, {
        team_config: nextConfig,
      });
      toast.success("Team configuration updated");
      setEditOpen(false);
      onUpdate?.();
    } catch (err) {
      toast.error("Failed to update team configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <Card className="border-dashed" data-testid="team-roster-panel">
      <CardHeader className="py-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="w-4 h-4" /> Audit Team
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {template_name} • {risk_appetite} appetite
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditOpen(true)}
            data-testid="edit-team-config-btn"
          >
            <Settings2 className="w-3 h-3 mr-1" /> Edit Team
          </Button>
          <Button
            size="sm"
            onClick={handleRunAnalysis}
            disabled={runningAnalysis}
            data-testid="run-team-analysis-btn"
          >
            {runningAnalysis ? (
              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {analysisPhase || "Analyzing..."}</>
            ) : (
              <><Play className="w-3 h-3 mr-1" /> {runButtonLabel}</>
            )}
          </Button>
        </div>
      </CardHeader>
      {runningAnalysis && activeAgentName && (
        <div className="px-4 pb-2 text-xs text-muted-foreground">
          Running: {activeAgentName}
        </div>
      )}
      <CardContent className="py-2">
        <div className="space-y-2">
          {agents.map((agent) => {
            const IconComponent = AGENT_ICONS[agent.icon] || User;
            const statusColor = STATUS_COLORS[agent.status] || STATUS_COLORS.idle;
            const statusLabel = STATUS_LABELS[agent.status] || "Unknown";
            const isMuted = agent.status === "muted";

            return (
              <div
                key={agent.agent_id}
                className={`flex items-center gap-3 p-2 rounded-lg transition-all ${isMuted ? "opacity-50 bg-muted/30" : "bg-muted/10 hover:bg-muted/20"
                  }`}
                data-testid={`agent-${agent.agent_id}`}
              >
                <div className="relative">
                  <IconComponent className={`w-5 h-5 ${isMuted ? "text-muted-foreground" : ""}`} />
                  <div className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ${statusColor}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{agent.name}</span>
                    {agent.role === "lead_auditor" && (
                      <Badge variant="outline" className="text-xs">Lead</Badge>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    <span className="block text-xs text-muted-foreground">{statusLabel}</span>
                    {agent.last_run_summary && (
                      <span className="block truncate text-[11px] text-muted-foreground/80" title={agent.last_run_summary}>
                        {agent.last_run_summary}
                      </span>
                    )}
                  </div>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                      <MoreVertical className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => handleMuteAgent(agent.agent_id, agent.status)}
                      disabled={agent.role === "lead_auditor"}
                    >
                      {isMuted ? (
                        <><Volume2 className="w-3 h-3 mr-2" /> Unmute</>
                      ) : (
                        <><VolumeX className="w-3 h-3 mr-2" /> Mute</>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      </CardContent>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Edit Audit Team</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto pr-2">
            <TeamConfigEditor
              initialTeamConfig={teamConfig}
              onCancel={() => setEditOpen(false)}
              onSave={handleSaveConfig}
              saveLabel={savingConfig ? "Saving..." : "Save Team"}
            />
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default TeamRosterPanel;
